import _ from 'lodash';

export type Mode = 'development' | 'production' | 'test';
export type Env = 'local' | 'development' | 'stage' | 'prod';

export interface AppConfig {
  apiUrl: string;
  mode: Mode;
  localesFolderHash: number;
  env: Env;
  useMockServer: boolean;
  sternumSDK: {
    protocolOpen: string;
  };
  app: {
    pollingInterval: number;
    documentationCenterLink: string;
    documentationSdksLink: string;
    documentationPrivacyLink: string;
    supportLink: string;
    documentationCenterSDKSetupGuideLink: string;
    documentationCenterSDKUsingPredefinedTraceWrappersLink: string;
    documentationSdkGettingStarted: string;
    documentationComputeGettingStarted: string;
    termsOfServicePath: string;
    termsOfServiceComputePath: string;
    serviceEmail: string;
  };
  google: {
    authClientId: string;
    recaptchaKey: string;
  };
  amCharts: {
    licenses: string[];
  };
  coralogix: {
    apiKey: string;
    appName: string;
    domain: 'EU1' | 'EU2' | 'US1' | 'US2' | 'AP1' | 'AP2';
  };
  featbit: {
    secretKey: string;
    userKeyId: string;
    streamingUri: string;
    eventsUri: string;
  };
  computeIntegrationsDeployCommands: {
    kubernetes: string;
    host: string;
  };
  encryptExportedWhitelistDataForClients?: string[];
  featureFlags?: {
    anomaliesOverTimeGraph?: boolean;
    alertsOverview?: boolean;
    registrationDevicesView?: boolean;
    registrationComputeView?: boolean;
    sandboxView?: boolean;
    cloudScanning?: boolean;
    postureManagement?: boolean;
    imageWidget?: boolean;
    deviceActivityInAnomalyInvestigation?: boolean;
    mockedAnomalies?: boolean;
    isOnPrem?: boolean;
    alertWhitelist?: boolean;
    limitMockedAnomaliesToClients?: string[];
    disableRecaptcha?: boolean;
    disableSSO?: boolean;
  };
}

const mode = import.meta.env.MODE as Mode;
const baseConfigFile = 'config.json';
const overrideConfigFile = 'config-override.json';

export const Config = await getConfig();

async function getConfig() {
  let finalConfig: AppConfig;
  let baseConfig: AppConfig;

  try {
    baseConfig = (await fetchConfig(baseConfigFile)) as AppConfig;
    finalConfig = baseConfig;
  } catch (err) {
    console.error(`Failed to load ${baseConfigFile} file`, err);
    throw new Error((err as { message: string }).message);
  }

  try {
    let localConfig = {};

    if (mode !== 'test') {
      localConfig = await fetchConfig(overrideConfigFile);
    }

    finalConfig = _.merge(baseConfig, localConfig);
  } catch {
    // absence of override config is ignored
    console.debug(`${overrideConfigFile} file was not loaded.`);
  }

  finalConfig.mode = mode;
  finalConfig.localesFolderHash = localesFolderHash;

  return finalConfig;
}

async function fetchConfig(name: string) {
  if (mode === 'test') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const file = require(`../../public/${name}`);

    return file;
  }

  const response = await fetch(`/${name}`);
  const config = await response.json();

  return config;
}
